/**
 * Embed Guest Message Service
 * Handles message storage and API calls for guest users in the embed widget
 *
 * This service reuses the CognitionMessage type from the main app for consistency.
 */

import { CognitionMessage } from '@/components/universal-messaging/components/AIThinkingPartner/types';
import type { TurnSource } from '@/core/apis/yourai/cognitionTurnSources';

import { debugLog } from '../debug';

// Re-export CognitionMessage for use in embed components. A streamed reply also carries the titled
// sources behind the answer (`turn_complete.sources`) — titles only, never identifiers.
export type EmbedCognitionMessage = CognitionMessage & { sources?: TurnSource[] };

interface GuestSession {
  threadId: string;
  messages: EmbedCognitionMessage[];
  lastActivity: string;
}

// ============================================================================
// Helper Functions
// ============================================================================

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

// ============================================================================
// Service Class
// ============================================================================

export class EmbedGuestMessageService {
  private static instance: EmbedGuestMessageService;
  private readonly STORAGE_KEY = 'embed_guest_cognition_sessions';
  private sessionCache: Record<string, GuestSession> | null = null;
  private appBaseUrl: string = '';
  private assetApiUrl: string = '';
  private token: string | null = null;

  private constructor() {}

  static getInstance(): EmbedGuestMessageService {
    if (!EmbedGuestMessageService.instance) {
      EmbedGuestMessageService.instance = new EmbedGuestMessageService();
    }
    return EmbedGuestMessageService.instance;
  }

  /**
   * Configure the service with API URLs and token
   * @param appBaseUrl - The app base URL (used for widget-proxy)
   * @param token - The auth token
   * @param assetApiUrl - The asset API URL (stored for reference, not used for API calls)
   */
  configure(appBaseUrl: string, token: string | null, assetApiUrl?: string): void {
    this.appBaseUrl = appBaseUrl;
    this.token = token;
    // Store assetApiUrl for reference (API calls always go through widget-proxy)
    this.assetApiUrl = assetApiUrl || '';
  }

  /**
   * Get or create a guest session for a cognition
   */
  getOrCreateSession(cognitionId: string): GuestSession {
    const sessions = this.getAllSessions();

    if (!sessions[cognitionId]) {
      sessions[cognitionId] = {
        threadId: generateId(),
        messages: [],
        lastActivity: new Date().toISOString(),
      };
      this.saveSessions(sessions);
    }

    return sessions[cognitionId];
  }

  /**
   * Get messages for a cognition
   */
  getMessages(cognitionId: string): EmbedCognitionMessage[] {
    const session = this.getOrCreateSession(cognitionId);
    return [...session.messages];
  }

  /**
   * Add an AI response message to the session
   */
  addAIResponse(cognitionId: string, response: EmbedCognitionMessage, replyToId: string): void {
    const session = this.getOrCreateSession(cognitionId);

    const aiMessage: EmbedCognitionMessage = {
      ...response,
      replyTo: replyToId || response.replyTo,
      myCognitionThread: {
        _id: session.threadId,
      },
    };

    // Check if a message with the same _id already exists (replace loading message)
    const existingIndex = session.messages.findIndex((m) => m._id === aiMessage._id);

    if (existingIndex >= 0) {
      // Preserve the replyTo from existing message if the new one doesn't have it
      if (!aiMessage.replyTo && session.messages[existingIndex].replyTo) {
        aiMessage.replyTo = session.messages[existingIndex].replyTo;
      }
      session.messages[existingIndex] = aiMessage;
    } else {
      session.messages.push(aiMessage);
    }

    session.lastActivity = new Date().toISOString();
    this.updateSession(cognitionId, session);
  }

  /**
   * Send a guest cognition message
   * Returns when the AI response is received (waits for response)
   *
   * @param cognitionId - The brain/cognition ID to use for the API call (must be valid)
   * @param cognitionName - Display name for the AI in messages
   * @param message - The user message to send
   * @param userId - User ID
   * @param userName - User display name
   * @param onUpdate - Callback when messages are updated
   * @param sessionKey - Optional session key for storage (defaults to cognitionId)
   * @param socketId - Socket ID for server to send response to the correct socket
   */
  async sendMessage(
    cognitionId: string,
    cognitionName: string,
    message: string,
    userId: string,
    userName: string,
    onUpdate?: (messages: EmbedCognitionMessage[]) => void,
    sessionKey?: string,
    socketId?: string | null,
  ): Promise<{
    threadId: string;
    messageId: string;
    loadingMessageId: string;
    response: EmbedCognitionMessage | null;
  }> {
    // Use sessionKey for storage if provided, otherwise use cognitionId
    const storageKey = sessionKey || cognitionId;
    const session = this.getOrCreateSession(storageKey);
    const messageId = generateId();
    const loadingMessageId = generateId();

    // Get all messages except the current one we're about to send
    const olderMessages = [...session.messages];

    // Add the user message to local storage first
    const userMessage: EmbedCognitionMessage = {
      _id: messageId,
      message,
      messageType: 'USER',
      contents: [],
      createdBy: {
        Id: userId,
        Name: userName,
        DisplayPic: '',
      },
      createdAt: new Date().toISOString(),
      read: true,
      myCognitionThread: {
        _id: session.threadId,
      },
    };

    session.messages.push(userMessage);

    // Create a loading message for the AI response
    const loadingMessage: EmbedCognitionMessage = {
      _id: loadingMessageId,
      message: '',
      messageType: 'BOT',
      loading: true,
      contents: [],
      createdBy: {
        Id: 'ai',
        Name: cognitionName || 'AI',
        DisplayPic: '',
      },
      createdAt: new Date().toISOString(),
      read: false,
      replyTo: messageId,
      myCognitionThread: {
        _id: session.threadId,
      },
      cognition: {
        _id: cognitionId,
        name: cognitionName,
      },
    };

    session.messages.push(loadingMessage);
    session.lastActivity = new Date().toISOString();
    this.updateSession(storageKey, session);

    // Notify update callback
    if (onUpdate) {
      onUpdate([...session.messages]);
    }

    try {
      // Always use widget-proxy for embed widget API calls
      // The widget can be embedded on any third-party domain, so we must proxy
      // requests through our server to avoid CORS issues
      const url = `${this.appBaseUrl}/api/widget-proxy/asset/threads/cognition-message-guest`;

      debugLog('[EmbedGuestMessage] Sending message to:', url);
      debugLog('[EmbedGuestMessage] Request payload:', {
        threadId: session.threadId,
        threadMessageId: loadingMessageId,
        cognitionId,
        messageLength: message.length,
        olderMessagesCount: olderMessages.length,
        socketId: socketId || 'not provided',
      });

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'X-Bolder-Widget': 'true',
        'X-Bolder-Widget-Origin': typeof window !== 'undefined' ? window.location.origin : '',
      };

      if (this.token) {
        headers['Authorization'] = `Bearer ${this.token}`;
      }

      const response = await fetch(url, {
        method: 'POST',
        headers,
        credentials: 'include', // Include cookies for session association
        body: JSON.stringify({
          threadId: session.threadId,
          threadMessageId: loadingMessageId,
          message,
          cognitionId,
          olderMessages,
          socketId, // Include socket ID so server knows where to send response
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to send message: ${response.status}`);
      }

      const responseData = await response.json();
      debugLog('[EmbedGuestMessage] API acknowledged:', responseData);

      // The API returns an acknowledgment ("Cognition request sent successfully")
      // The actual AI response will come via Socket.IO and will be stored in local storage
      // The polling mechanism in EmbedBrainPage will pick up the changes

      return {
        threadId: session.threadId,
        messageId,
        loadingMessageId,
        response: null,
      };
    } catch (error) {
      console.error('[EmbedGuestMessage] Failed to send message:', error);
      // Remove the loading message on error
      session.messages = session.messages.filter((m) => m._id !== loadingMessageId);
      this.updateSession(storageKey, session);

      if (onUpdate) {
        onUpdate([...session.messages]);
      }

      throw error;
    }
  }

  /**
   * Clear session for a specific cognition
   */
  clearSession(cognitionId: string): void {
    const sessions = this.getAllSessions();
    delete sessions[cognitionId];
    this.saveSessions(sessions);
  }

  /**
   * Get all sessions from session storage with caching
   */
  private getAllSessions(): Record<string, GuestSession> {
    if (typeof window === 'undefined') {
      return {};
    }

    if (this.sessionCache !== null) {
      return this.sessionCache;
    }

    try {
      const data = sessionStorage.getItem(this.STORAGE_KEY);
      const sessions = data ? JSON.parse(data) : {};
      this.sessionCache = sessions;
      return sessions;
    } catch (error) {
      console.error('[EmbedGuestMessage] Failed to parse sessions:', error);
      const emptySessions = {};
      this.sessionCache = emptySessions;
      return emptySessions;
    }
  }

  /**
   * Save sessions to session storage and update cache
   */
  private saveSessions(sessions: Record<string, GuestSession>): void {
    if (typeof window === 'undefined') {
      return;
    }

    this.sessionCache = sessions;

    try {
      sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(sessions));
    } catch (error) {
      console.error('[EmbedGuestMessage] Failed to save sessions:', error);
    }
  }

  /**
   * Update a specific session
   */
  private updateSession(cognitionId: string, session: GuestSession): void {
    const sessions = this.getAllSessions();
    sessions[cognitionId] = session;
    this.saveSessions(sessions);
  }
}

export default EmbedGuestMessageService;
